/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/LaneActionsPlus.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-ACTIONSPLUS-001
 * PURPOSE: Collapse a lane's action-only affordances behind ONE "+" so the
 *          lane rests as data, not as a row of buttons.
 * ROLE IN ARCHITECTURE: Presentational leaf shared by the lane frames.
 * MAIN EXPORTS: LaneActionsPlus.
 * NON-RESPONSIBILITIES: Owns no action logic — children keep their own state
 *   and popovers; this only decides whether they are mounted.
 *
 * NOTES FOR FUTURE AI: The distinction this encodes is affordance vs FACT. A
 * chip that states something true about the email ("Quotech HQ", "AP ·
 * Received") earns its resting place. A button that merely offers a mutation
 * ("Move org", "Refer") does not — four lanes of those read as clutter to the
 * operator, who could not find the actual content among them (2026-07-25: "so
 * cluttered ugly"). Children mount only when expanded, so a control that
 * self-hides after a prefetch (RehomeOrgControl for single-org members) also
 * stops paying its request on every email open.
 */
import { useState, type ReactNode } from 'react'
import { Plus, X } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'

export interface LaneActionsPlusProps {
  /** Announced on the toggle, e.g. "Filed to actions". */
  label: string
  /** Action controls, mounted only while expanded. */
  children: ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * One "+" that reveals a lane's action controls inline.
 *
 * Inputs: an a11y label and the action children.
 * Output: the collapsed "+" button, or the expanded run plus a close control.
 * Failure mode: none — collapsing simply unmounts the children.
 */
export function LaneActionsPlus({
  label,
  children,
  className,
  'data-testid': testId = 'lane-actions-plus',
}: LaneActionsPlusProps) {
  const [open, setOpen] = useState(false)

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        aria-expanded={false}
        title={label}
        data-testid={testId}
        className={cn(
          'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded',
          'text-muted-foreground/60 ring-1 ring-border/60 transition-colors',
          'hover:bg-muted hover:text-foreground',
          // 18px is a fine-pointer size; on touch it is half the 44px floor.
          // Same growth EllipsisSlot uses, and the row is already 44px there.
          'pointer-coarse:h-11 pointer-coarse:w-11',
          className,
        )}
      >
        <Plus className="h-3 w-3" aria-hidden />
      </button>
    )
  }

  return (
    <span
      className={cn('inline-flex shrink-0 items-center gap-1', className)}
      data-testid={`${testId}-expanded`}
    >
      {children}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label={`Hide ${label.toLowerCase()}`}
        aria-expanded
        data-testid={`${testId}-close`}
        className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground pointer-coarse:h-11 pointer-coarse:w-11"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  )
}
